import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useNavigate } from 'react-router-dom';
type Phase = 'baseline-collection' | 'baseline-output' | 'transition-to-diagnosis' | 'diagnostic-questions' | 'full-report';
export function DeepSelfDecode() {
  const navigate = useNavigate();
  // TODO: Check if user is authenticated and has baseline
  const [isAuthenticated] = useState(true);
  const [hasBaseline] = useState(false);
  // Start at baseline-collection if no baseline exists
  const [currentPhase, setCurrentPhase] = useState<Phase>(hasBaseline ? 'baseline-output' : 'baseline-collection');
  const [showBaselineExplanation, setShowBaselineExplanation] = useState(false);
  const [showWhatChanged, setShowWhatChanged] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatingMessage, setGeneratingMessage] = useState('');
  const [questionProgress, setQuestionProgress] = useState(0);
  // Baseline data (reusing QuickSelfDecode structure)
  const [baselineData, setBaselineData] = useState({
    dateOfBirth: '1990-01-15',
    timeOfBirth: '',
    timeKnown: 'not-sure',
    placeOfBirth: '',
    country: '',
    year: '1990',
    month: '01',
    day: '15',
    hour: '12',
    minute: '00',
    period: 'AM'
  });
  // Diagnostic questions data
  const [diagnosticData, setDiagnosticData] = useState({
    patternRepetition: [] as string[],
    formationConditions: [] as string[],
    constraintMechanisms: [] as string[],
    reinforcementLoops: [] as string[],
    awarenessTiming: '',
    triggerIntensity: '',
    responsePattern: '',
    contextualInput: ''
  });
  // Baseline output (structural foundation)
  const baselineOutput = {
    structuralOrientation: 'You are wired to observe and stabilize systems before fully entering them. When complexity rises, you move toward clarity — understanding patterns, naming dynamics, and organizing what feels scattered. This orientation is structural, not learned. It shapes how you process information, make decisions, and stabilize under pressure.',
    primaryTension: 'At the core of this system is a recurring pull between control and surrender — between architecting reality with precision and letting experience move you without managing it. This tension is not a problem to solve. It is the engine that drives how you engage with complexity.',
    structuralBlindSpot: 'Clarity can sometimes feel like completion. Understanding a pattern may arrive faster than emotional or bodily integration. The system can mistake cognitive resolution for systemic change, leading to repeated loops where insight does not translate to different action.'
  };
  // Full diagnostic output
  const fullDiagnosticOutput = {
    patternMechanics: 'When uncertainty or complexity rises, the system automatically moves toward analysis and structure-building. The trigger is ambiguity; the response is to map, name, and organize. This creates temporary stability but can delay embodied action. The pattern reinforces itself: clarity feels like progress, which rewards the pattern, which makes it more automatic.',
    patternOrigin: 'This pattern likely formed in conditions where clarity was scarce and consequences were high. Early environments may have rewarded precision, punished ambiguity, or required you to stabilize chaos for others. The system learned that understanding = safety, and that pattern became structural.',
    constraintMap: 'The pattern stays active because: (1) it reliably reduces anxiety in the short term, (2) external systems reward analysis over embodied action, (3) the cost of the pattern (delayed action, emotional bypass) is less visible than the benefit (immediate clarity). The constraint is not external pressure — it is that the pattern works well enough to stay invisible.',
    crossContextView: {
      work: 'At work, this shows up as over-preparation, analysis paralysis, or needing full clarity before committing. You may delay decisions until you have "enough information," which often means more than necessary.',
      relationships: 'In relationships, this can appear as emotional distance, intellectualizing conflict, or needing to "figure out" feelings before expressing them. Intimacy may feel destabilizing because it resists clear mapping.',
      decisions: 'Major decisions trigger the pattern most intensely. You may loop in analysis, seek external validation disguised as "more data," or feel frozen between options because neither feels perfectly clear.'
    },
    whatChangedFromBaseline: {
      baseline: 'The baseline showed your structural orientation (observer-stabilizer), core tension (control vs surrender), and blind spot (clarity as completion).',
      diagnosis: 'The diagnostic questions revealed: how the pattern formed (early conditions requiring clarity for safety), how it reinforces itself (clarity = reward = more automatic), what keeps it active (short-term anxiety reduction), and where it repeats across contexts (work, relationships, decisions).',
      whyBaselineAlone: 'Baseline shows what kind of system this is. Diagnosis shows how the system formed, why it stays active, and where it creates friction or leverage. Baseline is structure; diagnosis is mechanism and repetition.'
    },
    whatChangedFromSnapshot: {
      quickSelfSnapshot: 'The Quick Self Decode showed your baseline pattern: over-preparation, analysis paralysis, and emotional distance. It identified the core tension (control vs surrender) and the blind spot (clarity as completion).',
      deepDiagnosis: 'The deep diagnosis revealed the pattern mechanics, origin, and constraint map. It showed how the pattern forms in early life, how it reinforces itself, and what keeps it active. It also identified the cross-context repetition and the boundary conditions.',
      whySnapshotInsufficient: 'The Quick Self Decode alone could not show the full picture. It identified the pattern but not the mechanics, origin, or constraint map. It could not explain why the pattern repeats across contexts or why it stays active despite awareness.'
    }
  };
  const currentYear = new Date().getFullYear();
  const years = Array.from({
    length: currentYear - 1919
  }, (_, i) => currentYear - i);
  const months = [{
    value: '01',
    label: 'January'
  }, {
    value: '02',
    label: 'February'
  }, {
    value: '03',
    label: 'March'
  }, {
    value: '04',
    label: 'April'
  }, {
    value: '05',
    label: 'May'
  }, {
    value: '06',
    label: 'June'
  }, {
    value: '07',
    label: 'July'
  }, {
    value: '08',
    label: 'August'
  }, {
    value: '09',
    label: 'September'
  }, {
    value: '10',
    label: 'October'
  }, {
    value: '11',
    label: 'November'
  }, {
    value: '12',
    label: 'December'
  }];
  const getDaysInMonth = (year: string, month: string) => {
    if (!year || !month) return 31;
    return new Date(parseInt(year), parseInt(month), 0).getDate();
  };
  const updateDateOfBirth = (year: string, month: string, day: string) => {
    if (year && month && day) {
      const dateString = `${year}-${month}-${day}`;
      setBaselineData(prev => ({
        ...prev,
        year,
        month,
        day,
        dateOfBirth: dateString
      }));
    } else {
      setBaselineData(prev => ({
        ...prev,
        year,
        month,
        day,
        dateOfBirth: ''
      }));
    }
  };
  const convertTo24Hour = (hour: string, period: string): string => {
    let h = parseInt(hour);
    if (period === 'PM' && h !== 12) h += 12;
    if (period === 'AM' && h === 12) h = 0;
    return h.toString().padStart(2, '0');
  };
  const updateTimeOfBirth = (hour: string, minute: string, period: string) => {
    const hour24 = convertTo24Hour(hour, period);
    const timeString = `${hour24}:${minute}`;
    setBaselineData(prev => ({
      ...prev,
      hour,
      minute,
      period,
      timeOfBirth: timeString
    }));
  };
  const handleBaselineSubmit = () => {
    if (!baselineData.dateOfBirth || !baselineData.placeOfBirth || !baselineData.country) return;
    if ((baselineData.timeKnown === 'exact' || baselineData.timeKnown === 'approximate') && !baselineData.timeOfBirth) return;
    setIsGenerating(true);
    setGeneratingMessage('Generating your structural baseline...');
    setTimeout(() => {
      setIsGenerating(false);
      setCurrentPhase('baseline-output');
    }, 2000);
  };
  const handleStartDiagnosis = () => {
    setCurrentPhase('transition-to-diagnosis');
  };
  const handleBeginQuestions = () => {
    setCurrentPhase('diagnostic-questions');
    setQuestionProgress(0);
  };
  const handleDiagnosticSubmit = () => {
    if (!diagnosticData.awarenessTiming || !diagnosticData.triggerIntensity || !diagnosticData.responsePattern) return;
    if (diagnosticData.patternRepetition.length === 0 || diagnosticData.formationConditions.length === 0) return;
    setIsGenerating(true);
    setGeneratingMessage('Generating full diagnostic...');
    setTimeout(() => {
      setIsGenerating(false);
      setCurrentPhase('full-report');
    }, 3000);
  };
  const handleToggleMultiSelect = (field: keyof typeof diagnosticData, value: string) => {
    setDiagnosticData(prev => {
      const currentArray = prev[field] as string[];
      return {
        ...prev,
        [field]: currentArray.includes(value) ? currentArray.filter(v => v !== value) : [...currentArray, value]
      };
    });
  };
  if (isGenerating) {
    return <>
        <Helmet data-id="element-913">
          <title data-id="element-914">Generating Diagnostic | Lazy Monk Labs</title>
        </Helmet>
        <div className="min-h-screen bg-[#0A0A0A] text-[#F5F5F5] flex items-center justify-center px-6" data-id="element-915">
          <div className="text-center" data-id="element-916">
            <div className="mb-6" data-id="element-917">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[#F5F5F5]" data-id="element-918"></div>
            </div>
            <p className="text-lg text-[#F5F5F5]/70" data-id="element-919">{generatingMessage}</p>
          </div>
        </div>
      </>;
  }
  return <>
      <Helmet data-id="element-920">
        <title data-id="element-921">Deep Self Decode | Lazy Monk Labs</title>
      </Helmet>
      <div className="min-h-screen bg-[#0A0A0A] text-[#F5F5F5]" data-id="element-922">
        <header data-id="element-923">
          <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0A0A0A]/95 backdrop-blur-sm border-b border-[#2B2B2B]" data-id="element-924">
            <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between" data-id="element-925">
              <Link to="/" className="flex items-center gap-3 hover:opacity-80 transition-opacity" data-id="element-926">
                <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/jXd6KyuddrgAfqwwkkpUrR/Lazy_Monk_Labs_Logo.png" alt="Lazy Monk Labs Logo" className="h-8 w-auto" width="32" height="32" data-id="element-927" />
                <span className="text-lg font-medium tracking-tight" data-id="element-928">
                  Lazy Monk Labs
                </span>
              </Link>
              <Link to="/decode" className="text-sm text-[#F5F5F5]/80 hover:text-[#F5F5F5] transition-colors" data-id="element-929">
                Back to Decode
              </Link>
            </div>
          </nav>
        </header>

        <main className="pt-24 pb-16 px-6" data-id="element-930">
          <div className="max-w-2xl mx-auto" data-id="element-931">
            {/* PHASE 1: Baseline Collection */}
            {currentPhase === 'baseline-collection' && <div data-id="element-932">
                <div className="mb-12" data-id="element-933">
                  <h1 className="text-3xl md:text-4xl font-medium mb-3 leading-tight tracking-tight" data-id="element-934">
                    Deep Self Decode
                  </h1>
                  <p className="text-sm text-[#F5F5F5]/50" data-id="element-935">
                    Structural baseline
                  </p>
                </div>

                {/* Reuse baseline collection UI from QuickSelfDecode */}
                <div className="space-y-8 mb-12" data-id="element-936">
                  <div data-id="element-937">
                    <label className="block text-sm font-medium mb-3" data-id="element-938">
                      Date of birth
                    </label>
                    {baselineData.dateOfBirth && <div className="mb-4 p-4 bg-[#111214] border border-[#2B2B2B] text-center" data-id="element-939">
                        <div className="text-2xl font-medium" data-id="element-940">
                          {months.find(m => m.value === baselineData.month)?.label}{' '}
                          {baselineData.day}, {baselineData.year}
                        </div>
                      </div>}
                    <div className="grid grid-cols-3 gap-3" data-id="element-941">
                      <div data-id="element-942">
                        <label htmlFor="month" className="block text-xs font-medium mb-2 text-[#F5F5F5]/60 uppercase tracking-wider" data-id="element-943">
                          Month
                        </label>
                        <select id="month" value={baselineData.month} onChange={e => updateDateOfBirth(baselineData.year, e.target.value, baselineData.day)} className="w-full px-3 py-3 bg-[#0A0A0A] border border-[#2B2B2B] text-[#F5F5F5] text-sm focus:border-[#F5F5F5]/50 focus:outline-none transition-colors" data-id="element-944">
                          <option value="" data-id="element-945">Month</option>
                          {months.map(m => <option key={m.value} value={m.value} data-id="element-946">
                              {m.label}
                            </option>)}
                        </select>
                      </div>
                      <div data-id="element-947">
                        <label htmlFor="day" className="block text-xs font-medium mb-2 text-[#F5F5F5]/60 uppercase tracking-wider" data-id="element-948">
                          Day
                        </label>
                        <select id="day" value={baselineData.day} onChange={e => updateDateOfBirth(baselineData.year, baselineData.month, e.target.value)} disabled={!baselineData.month || !baselineData.year} className="w-full px-3 py-3 bg-[#0A0A0A] border border-[#2B2B2B] text-[#F5F5F5] text-sm focus:border-[#F5F5F5]/50 focus:outline-none transition-colors disabled:opacity-30" data-id="element-949">
                          <option value="" data-id="element-950">Day</option>
                          {Array.from({
                        length: getDaysInMonth(baselineData.year, baselineData.month)
                      }, (_, i) => i + 1).map(d => <option key={d} value={d.toString().padStart(2, '0')} data-id="element-951">
                              {d}
                            </option>)}
                        </select>
                      </div>
                      <div data-id="element-952">
                        <label htmlFor="year" className="block text-xs font-medium mb-2 text-[#F5F5F5]/60 uppercase tracking-wider" data-id="element-953">
                          Year
                        </label>
                        <select id="year" value={baselineData.year} onChange={e => updateDateOfBirth(e.target.value, baselineData.month, baselineData.day)} className="w-full px-3 py-3 bg-[#0A0A0A] border border-[#2B2B2B] text-[#F5F5F5] text-sm focus:border-[#F5F5F5]/50 focus:outline-none transition-colors" data-id="element-954">
                          <option value="" data-id="element-955">Year</option>
                          {years.map(y => <option key={y} value={y} data-id="element-956">
                              {y}
                            </option>)}
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* Time of Birth - Same as QuickSelfDecode */}
                  <div data-id="element-957">
                    <label className="block text-sm font-medium mb-3" data-id="element-958">
                      Time of birth{' '}
                      <span className="text-[#F5F5F5]/40" data-id="element-959">(optional)</span>
                    </label>
                    <div className="space-y-3 mb-4" data-id="element-960">
                      {[{
                    value: 'exact',
                    label: 'I know the exact time'
                  }, {
                    value: 'approximate',
                    label: 'I know approximately'
                  }, {
                    value: 'not-sure',
                    label: "Not sure / Don't know"
                  }].map(option => <label key={option.value} className={`block border p-4 cursor-pointer transition-colors ${baselineData.timeKnown === option.value ? 'border-[#F5F5F5] bg-[#F5F5F5]/5' : 'border-[#2B2B2B] hover:border-[#F5F5F5]/30'}`} data-id="element-961">
                          <input type="radio" name="timeKnown" value={option.value} checked={baselineData.timeKnown === option.value} onChange={e => setBaselineData(prev => ({
                      ...prev,
                      timeKnown: e.target.value,
                      timeOfBirth: '',
                      hour: '12',
                      minute: '00',
                      period: 'AM'
                    }))} className="sr-only" data-id="element-962" />
                          <span className="text-sm" data-id="element-963">{option.label}</span>
                        </label>)}
                    </div>
                    {(baselineData.timeKnown === 'exact' || baselineData.timeKnown === 'approximate') && <div className="mt-6" data-id="element-964">
                        <div className="bg-[#111214] border border-[#2B2B2B] p-8" data-id="element-965">
                          <div className="text-center mb-8" data-id="element-966">
                            <div className="text-4xl font-medium tracking-tight" data-id="element-967">
                              {baselineData.hour}:{baselineData.minute}{' '}
                              {baselineData.period}
                            </div>
                            <p className="text-xs text-[#F5F5F5]/40 mt-2" data-id="element-968">
                              {baselineData.timeKnown === 'approximate' ? 'Your best estimate is fine' : 'Select the exact time'}
                            </p>
                          </div>
                          <div className="mb-6" data-id="element-969">
                            <label className="block text-xs font-medium mb-3 text-[#F5F5F5]/60 uppercase tracking-wider" data-id="element-970">
                              Hour
                            </label>
                            <div className="grid grid-cols-6 gap-2" data-id="element-971">
                              {['12', '01', '02', '03', '04', '05', '06', '07', '08', '09', '10', '11'].map(h => <button key={h} type="button" onClick={() => updateTimeOfBirth(h, baselineData.minute, baselineData.period)} className={`py-3 text-sm font-medium transition-colors ${baselineData.hour === h ? 'bg-[#F5F5F5] text-[#0A0A0A]' : 'bg-[#0A0A0A] border border-[#2B2B2B] hover:border-[#F5F5F5]/30'}`} data-id="element-972">
                                  {h}
                                </button>)}
                            </div>
                          </div>
                          <div className="mb-6" data-id="element-973">
                            <label className="block text-xs font-medium mb-3 text-[#F5F5F5]/60 uppercase tracking-wider" data-id="element-974">
                              Minute
                            </label>
                            <div className="grid grid-cols-6 gap-2" data-id="element-975">
                              {['00', '15', '30', '45'].map(m => <button key={m} type="button" onClick={() => updateTimeOfBirth(baselineData.hour, m, baselineData.period)} className={`py-3 text-sm font-medium transition-colors ${baselineData.minute === m ? 'bg-[#F5F5F5] text-[#0A0A0A]' : 'bg-[#0A0A0A] border border-[#2B2B2B] hover:border-[#F5F5F5]/30'}`} data-id="element-976">
                                  {m}
                                </button>)}
                            </div>
                            <div className="mt-2" data-id="element-977">
                              <input type="number" min="0" max="59" value={baselineData.minute} onChange={e => {
                          const val = e.target.value.padStart(2, '0');
                          if (parseInt(val) >= 0 && parseInt(val) <= 59) updateTimeOfBirth(baselineData.hour, val, baselineData.period);
                        }} placeholder="Or type exact minute" className="w-full px-4 py-2 bg-[#0A0A0A] border border-[#2B2B2B] text-[#F5F5F5] text-sm placeholder-[#F5F5F5]/40 focus:border-[#F5F5F5]/50 focus:outline-none transition-colors" data-id="element-978" />
                            </div>
                          </div>
                          <div data-id="element-979">
                            <label className="block text-xs font-medium mb-3 text-[#F5F5F5]/60 uppercase tracking-wider" data-id="element-980">
                              Period
                            </label>
                            <div className="grid grid-cols-2 gap-2" data-id="element-981">
                              {['AM', 'PM'].map(p => <button key={p} type="button" onClick={() => updateTimeOfBirth(baselineData.hour, baselineData.minute, p)} className={`py-3 text-sm font-medium transition-colors ${baselineData.period === p ? 'bg-[#F5F5F5] text-[#0A0A0A]' : 'bg-[#0A0A0A] border border-[#2B2B2B] hover:border-[#F5F5F5]/30'}`} data-id="element-982">
                                  {p}
                                </button>)}
                            </div>
                          </div>
                        </div>
                      </div>}
                  </div>

                  <div data-id="element-983">
                    <label htmlFor="placeOfBirth" className="block text-sm font-medium mb-2" data-id="element-984">
                      Place of birth
                    </label>
                    <input type="text" id="placeOfBirth" value={baselineData.placeOfBirth} onChange={e => setBaselineData(prev => ({
                  ...prev,
                  placeOfBirth: e.target.value
                }))} placeholder="City or town" className="w-full px-4 py-3 bg-[#0A0A0A] border border-[#2B2B2B] text-[#F5F5F5] placeholder-[#F5F5F5]/40 focus:border-[#F5F5F5]/50 focus:outline-none transition-colors" data-id="element-985" />
                  </div>

                  <div data-id="element-986">
                    <label htmlFor="country" className="block text-sm font-medium mb-2" data-id="element-987">
                      Country
                    </label>
                    <input type="text" id="country" value={baselineData.country} onChange={e => setBaselineData(prev => ({
                  ...prev,
                  country: e.target.value
                }))} placeholder="Country" className="w-full px-4 py-3 bg-[#0A0A0A] border border-[#2B2B2B] text-[#F5F5F5] placeholder-[#F5F5F5]/40 focus:border-[#F5F5F5]/50 focus:outline-none transition-colors" data-id="element-988" />
                  </div>
                </div>

                <button onClick={handleBaselineSubmit} disabled={!baselineData.dateOfBirth || !baselineData.placeOfBirth || !baselineData.country || (baselineData.timeKnown === 'exact' || baselineData.timeKnown === 'approximate') && !baselineData.timeOfBirth} className="w-full px-8 py-5 bg-[#F5F5F5] text-[#0A0A0A] font-semibold text-sm tracking-widest uppercase hover:bg-[#F5F5F5]/90 transition-colors shadow-lg disabled:opacity-30 disabled:cursor-not-allowed" data-id="element-989">
                  Generate Structural Baseline
                </button>
              </div>}

            {/* PHASE 2: Baseline Output (Deep Version) */}
            {currentPhase === 'baseline-output' && <div data-id="element-990">
                <div className="mb-12" data-id="element-991">
                  <h1 className="text-3xl md:text-4xl font-medium mb-3 leading-tight tracking-tight" data-id="element-992">
                    Deep Self Decode
                  </h1>
                  <p className="text-sm text-[#F5F5F5]/50" data-id="element-993">
                    Structural baseline
                  </p>
                </div>

                <div className="mb-12 pb-12 border-b border-[#2B2B2B]" data-id="element-994">
                  <div className="flex items-start gap-4 mb-4" data-id="element-995">
                    <span className="text-2xl" data-id="element-996">🧠</span>
                    <div className="flex-1" data-id="element-997">
                      <h2 className="text-xl font-medium mb-3" data-id="element-998">
                        Structural Orientation
                      </h2>
                      <p className="text-[#F5F5F5]/70 leading-relaxed" data-id="element-999">
                        {baselineOutput.structuralOrientation}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="mb-12 pb-12 border-b border-[#2B2B2B]" data-id="element-1000">
                  <div className="flex items-start gap-4 mb-4" data-id="element-1001">
                    <span className="text-2xl" data-id="element-1002">⚖️</span>
                    <div className="flex-1" data-id="element-1003">
                      <h2 className="text-xl font-medium mb-3" data-id="element-1004">
                        Primary Tension Axis
                      </h2>
                      <p className="text-[#F5F5F5]/70 leading-relaxed" data-id="element-1005">
                        {baselineOutput.primaryTension}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="mb-12 pb-12 border-b border-[#2B2B2B]" data-id="element-1006">
                  <div className="flex items-start gap-4 mb-4" data-id="element-1007">
                    <span className="text-2xl" data-id="element-1008">⚠️</span>
                    <div className="flex-1" data-id="element-1009">
                      <h2 className="text-xl font-medium mb-3" data-id="element-1010">
                        Structural Blind Spot
                      </h2>
                      <p className="text-[#F5F5F5]/70 leading-relaxed" data-id="element-1011">
                        {baselineOutput.structuralBlindSpot}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="mb-12 text-center" data-id="element-1012">
                  <p className="text-xs text-[#F5F5F5]/40 uppercase tracking-wider" data-id="element-1013">
                    Structural Baseline — remains constant across time
                  </p>
                </div>

                <div className="mb-12" data-id="element-1014">
                  <button onClick={() => setShowBaselineExplanation(!showBaselineExplanation)} className="flex items-center gap-2 text-sm text-[#F5F5F5]/70 hover:text-[#F5F5F5] transition-colors" data-id="element-1015">
                    <span data-id="element-1016">📘</span>
                    <span data-id="element-1017">How this report is generated</span>
                    <span className="text-xs" data-id="element-1018">
                      {showBaselineExplanation ? '▼' : '▶'}
                    </span>
                  </button>
                  {showBaselineExplanation && <div className="mt-4 p-6 bg-[#111214] border border-[#2B2B2B]" data-id="element-1019">
                      <p className="text-sm text-[#F5F5F5]/70 leading-relaxed" data-id="element-1020">
                        This report starts with your structural baseline — the
                        parts of you that do not change with mood or
                        circumstance. Birth data is used as coordinates to map
                        how this system processes information and stabilizes
                        tension. The next questions do not change structure.
                        They diagnose how this structure formed, repeats, and
                        stays active.
                      </p>
                    </div>}
                </div>

                <button onClick={handleStartDiagnosis} className="w-full px-8 py-5 bg-[#F5F5F5] text-[#0A0A0A] font-semibold text-sm tracking-widest uppercase hover:bg-[#F5F5F5]/90 transition-colors shadow-lg" data-id="element-1021">
                  Continue to Diagnosis
                </button>
              </div>}

            {/* PHASE 3: Transition to Diagnosis */}
            {currentPhase === 'transition-to-diagnosis' && <div data-id="element-1022">
                <div className="mb-12" data-id="element-1023">
                  <h1 className="text-3xl md:text-4xl font-medium mb-3 leading-tight tracking-tight" data-id="element-1024">
                    From structure to diagnosis
                  </h1>
                </div>

                <div className="mb-12" data-id="element-1025">
                  <p className="text-lg text-[#F5F5F5]/70 leading-relaxed mb-6" data-id="element-1026">
                    The baseline shows what kind of system this is.
                  </p>
                  <p className="text-lg text-[#F5F5F5]/70 leading-relaxed" data-id="element-1027">
                    The next questions diagnose how this system formed, repeats
                    across life, and stays active.
                  </p>
                </div>

                <button onClick={handleBeginQuestions} className="w-full px-8 py-5 bg-[#F5F5F5] text-[#0A0A0A] font-semibold text-sm tracking-widest uppercase hover:bg-[#F5F5F5]/90 transition-colors shadow-lg" data-id="element-1028">
                  Begin Diagnostic Questions
                </button>
              </div>}

            {/* PHASE 4: Diagnostic Questions */}
            {currentPhase === 'diagnostic-questions' && <div data-id="element-1029">
                <div className="mb-12" data-id="element-1030">
                  <h1 className="text-3xl md:text-4xl font-medium mb-3 leading-tight tracking-tight" data-id="element-1031">
                    Diagnostic Questions
                  </h1>
                  <div className="flex items-center gap-2 text-xs text-[#F5F5F5]/40" data-id="element-1032">
                    <div className="flex-1 bg-[#2B2B2B] h-1 rounded-full overflow-hidden" data-id="element-1033">
                      <div className="bg-[#F5F5F5] h-full transition-all" style={{
                    width: `${questionProgress / 8 * 100}%`
                  }} data-id="element-1034"></div>
                    </div>
                    <span data-id="element-1035">{questionProgress} of 8</span>
                  </div>
                </div>

                <div className="space-y-12 mb-12" data-id="element-1036">
                  {/* Question 1: Pattern Repetition */}
                  <div data-id="element-1037">
                    <h2 className="text-lg font-medium mb-3" data-id="element-1038">
                      Where does this pattern show up most in your life?
                    </h2>
                    <p className="text-sm text-[#F5F5F5]/50 mb-6" data-id="element-1039">
                      Select all that apply
                    </p>
                    <div className="space-y-3" data-id="element-1040">
                      {['Work and professional decisions', 'Close relationships', 'Major life transitions', 'Daily routines and habits', 'Conflict or high-pressure situations'].map(option => <label key={option} className={`block border p-5 cursor-pointer transition-colors ${diagnosticData.patternRepetition.includes(option) ? 'border-[#F5F5F5] bg-[#F5F5F5]/5' : 'border-[#2B2B2B] hover:border-[#F5F5F5]/30'}`} data-id="element-1041">
                          <input type="checkbox" checked={diagnosticData.patternRepetition.includes(option)} onChange={() => handleToggleMultiSelect('patternRepetition', option)} className="sr-only" data-id="element-1042" />
                          <span className="text-base" data-id="element-1043">{option}</span>
                        </label>)}
                    </div>
                  </div>

                  {/* Question 2: Formation Conditions */}
                  <div data-id="element-1044">
                    <h2 className="text-lg font-medium mb-3" data-id="element-1045">
                      What conditions in your early life may have shaped this
                      pattern?
                    </h2>
                    <p className="text-sm text-[#F5F5F5]/50 mb-6" data-id="element-1046">
                      Select all that apply
                    </p>
                    <div className="space-y-3" data-id="element-1047">
                      {['Environments that rewarded clarity and precision', 'Situations where ambiguity felt unsafe', 'Responsibility for stabilizing chaos or others', 'High consequences for mistakes or uncertainty', 'Limited emotional support or validation'].map(option => <label key={option} className={`block border p-5 cursor-pointer transition-colors ${diagnosticData.formationConditions.includes(option) ? 'border-[#F5F5F5] bg-[#F5F5F5]/5' : 'border-[#2B2B2B] hover:border-[#F5F5F5]/30'}`} data-id="element-1048">
                          <input type="checkbox" checked={diagnosticData.formationConditions.includes(option)} onChange={() => handleToggleMultiSelect('formationConditions', option)} className="sr-only" data-id="element-1049" />
                          <span className="text-base" data-id="element-1050">{option}</span>
                        </label>)}
                    </div>
                  </div>

                  {/* Question 3: Awareness Timing */}
                  <div data-id="element-1051">
                    <h2 className="text-lg font-medium mb-6" data-id="element-1052">
                      When do you typically become aware of this pattern?
                    </h2>
                    <div className="space-y-3" data-id="element-1053">
                      {['Before it activates (I can see it coming)', "While it's happening (I notice in real-time)", "After it's over (only in reflection)", 'Rarely aware of it at all'].map(option => <label key={option} className={`block border p-5 cursor-pointer transition-colors ${diagnosticData.awarenessTiming === option ? 'border-[#F5F5F5] bg-[#F5F5F5]/5' : 'border-[#2B2B2B] hover:border-[#F5F5F5]/30'}`} data-id="element-1054">
                          <input type="radio" name="awarenessTiming" value={option} checked={diagnosticData.awarenessTiming === option} onChange={e => setDiagnosticData(prev => ({
                      ...prev,
                      awarenessTiming: e.target.value
                    }))} className="sr-only" data-id="element-1055" />
                          <span className="text-base" data-id="element-1056">{option}</span>
                        </label>)}
                    </div>
                  </div>

                  {/* Question 4: Trigger Intensity */}
                  <div data-id="element-1057">
                    <h2 className="text-lg font-medium mb-6" data-id="element-1058">
                      How intense does the trigger need to be for this pattern
                      to activate?
                    </h2>
                    <div className="space-y-3" data-id="element-1059">
                      {['Very low (activates easily)', 'Moderate (needs some pressure)', 'High (only under significant stress)', 'Extreme (only in crisis)'].map(option => <label key={option} className={`block border p-5 cursor-pointer transition-colors ${diagnosticData.triggerIntensity === option ? 'border-[#F5F5F5] bg-[#F5F5F5]/5' : 'border-[#2B2B2B] hover:border-[#F5F5F5]/30'}`} data-id="element-1060">
                          <input type="radio" name="triggerIntensity" value={option} checked={diagnosticData.triggerIntensity === option} onChange={e => setDiagnosticData(prev => ({
                      ...prev,
                      triggerIntensity: e.target.value
                    }))} className="sr-only" data-id="element-1061" />
                          <span className="text-base" data-id="element-1062">{option}</span>
                        </label>)}
                    </div>
                  </div>

                  {/* Question 5: Response Pattern */}
                  <div data-id="element-1063">
                    <h2 className="text-lg font-medium mb-6" data-id="element-1064">
                      What do you typically do when this pattern activates?
                    </h2>
                    <div className="space-y-3" data-id="element-1065">
                      {['Analyze and plan extensively', 'Withdraw and process internally', 'Seek external validation or input', 'Push through with action', 'Freeze or avoid the situation'].map(option => <label key={option} className={`block border p-5 cursor-pointer transition-colors ${diagnosticData.responsePattern === option ? 'border-[#F5F5F5] bg-[#F5F5F5]/5' : 'border-[#2B2B2B] hover:border-[#F5F5F5]/30'}`} data-id="element-1066">
                          <input type="radio" name="responsePattern" value={option} checked={diagnosticData.responsePattern === option} onChange={e => setDiagnosticData(prev => ({
                      ...prev,
                      responsePattern: e.target.value
                    }))} className="sr-only" data-id="element-1067" />
                          <span className="text-base" data-id="element-1068">{option}</span>
                        </label>)}
                    </div>
                  </div>

                  {/* Question 6: Contextual Input (Free Text) */}
                  <div data-id="element-1069">
                    <h2 className="text-lg font-medium mb-3" data-id="element-1070">
                      Anything important this diagnostic should factor in?
                    </h2>
                    <p className="text-sm text-[#F5F5F5]/50 mb-4" data-id="element-1071">
                      A situation, relationship, or theme you're currently
                      navigating.
                    </p>
                    <textarea value={diagnosticData.contextualInput} onChange={e => setDiagnosticData(prev => ({
                  ...prev,
                  contextualInput: e.target.value
                }))} rows={4} maxLength={300} className="w-full px-4 py-3 bg-[#0A0A0A] border border-[#2B2B2B] text-[#F5F5F5] placeholder-[#F5F5F5]/40 focus:border-[#F5F5F5]/50 focus:outline-none transition-colors resize-none" placeholder="Optional context..." data-id="element-1072" />
                    <p className="text-xs text-[#F5F5F5]/40 mt-2 text-right" data-id="element-1073">
                      {diagnosticData.contextualInput.length}/300
                    </p>
                  </div>
                </div>

                <button onClick={handleDiagnosticSubmit} disabled={!diagnosticData.awarenessTiming || !diagnosticData.triggerIntensity || !diagnosticData.responsePattern || diagnosticData.patternRepetition.length === 0 || diagnosticData.formationConditions.length === 0} className="w-full px-8 py-5 bg-[#F5F5F5] text-[#0A0A0A] font-semibold text-sm tracking-widest uppercase hover:bg-[#F5F5F5]/90 transition-colors shadow-lg disabled:opacity-30 disabled:cursor-not-allowed" data-id="element-1074">
                  Generate Full Diagnostic
                </button>
              </div>}

            {/* PHASE 5: Full Report */}
            {currentPhase === 'full-report' && <div data-id="element-1075">
                <div className="mb-12" data-id="element-1076">
                  <h1 className="text-3xl md:text-4xl font-medium mb-3 leading-tight tracking-tight" data-id="element-1077">
                    Deep Self Decode
                  </h1>
                  <p className="text-sm text-[#F5F5F5]/50" data-id="element-1078">Full Diagnostic</p>
                </div>

                {/* Section 1: Structural Baseline (Condensed) */}
                <div className="mb-12 pb-12 border-b border-[#2B2B2B]" data-id="element-1079">
                  <div className="flex items-start gap-4 mb-4" data-id="element-1080">
                    <span className="text-2xl" data-id="element-1081">🧱</span>
                    <div className="flex-1" data-id="element-1082">
                      <p className="text-xs text-[#F5F5F5]/40 uppercase tracking-wider mb-3" data-id="element-1083">
                        Structural Baseline (Unchanged)
                      </p>
                      <p className="text-sm text-[#F5F5F5]/60 leading-relaxed" data-id="element-1084">
                        Observer-stabilizer orientation. Core tension: control
                        vs surrender. Blind spot: clarity as completion.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Section 2: Pattern Mechanics */}
                <div className="mb-12 pb-12 border-b border-[#2B2B2B]" data-id="element-1085">
                  <div className="flex items-start gap-4 mb-4" data-id="element-1086">
                    <span className="text-2xl" data-id="element-1087">🔁</span>
                    <div className="flex-1" data-id="element-1088">
                      <h2 className="text-xl font-medium mb-3" data-id="element-1089">
                        Pattern Mechanics
                      </h2>
                      <p className="text-[#F5F5F5]/70 leading-relaxed" data-id="element-1090">
                        {fullDiagnosticOutput.patternMechanics}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Section 3: Pattern Origin */}
                <div className="mb-12 pb-12 border-b border-[#2B2B2B]" data-id="element-1091">
                  <div className="flex items-start gap-4 mb-4" data-id="element-1092">
                    <span className="text-2xl" data-id="element-1093">🧬</span>
                    <div className="flex-1" data-id="element-1094">
                      <h2 className="text-xl font-medium mb-3" data-id="element-1095">
                        Pattern Origin
                      </h2>
                      <p className="text-[#F5F5F5]/70 leading-relaxed" data-id="element-1096">
                        {fullDiagnosticOutput.patternOrigin}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Section 4: Constraint Map */}
                <div className="mb-12 pb-12 border-b border-[#2B2B2B]" data-id="element-1097">
                  <div className="flex items-start gap-4 mb-4" data-id="element-1098">
                    <span className="text-2xl" data-id="element-1099">🔒</span>
                    <div className="flex-1" data-id="element-1100">
                      <h2 className="text-xl font-medium mb-3" data-id="element-1101">
                        Constraint Map
                      </h2>
                      <p className="text-[#F5F5F5]/70 leading-relaxed" data-id="element-1102">
                        {fullDiagnosticOutput.constraintMap}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Section 5: Cross-Context View */}
                <div className="mb-12 pb-12 border-b border-[#2B2B2B]" data-id="element-1103">
                  <div className="flex items-start gap-4 mb-4" data-id="element-1104">
                    <span className="text-2xl" data-id="element-1105">🌐</span>
                    <div className="flex-1" data-id="element-1106">
                      <h2 className="text-xl font-medium mb-6" data-id="element-1107">
                        Cross-Context View
                      </h2>
                      <div className="space-y-6" data-id="element-1108">
                        <div data-id="element-1109">
                          <h3 className="text-sm font-medium mb-2 text-[#F5F5F5]/80" data-id="element-1110">
                            Work
                          </h3>
                          <p className="text-[#F5F5F5]/70 leading-relaxed" data-id="element-1111">
                            {fullDiagnosticOutput.crossContextView.work}
                          </p>
                        </div>
                        <div data-id="element-1112">
                          <h3 className="text-sm font-medium mb-2 text-[#F5F5F5]/80" data-id="element-1113">
                            Relationships
                          </h3>
                          <p className="text-[#F5F5F5]/70 leading-relaxed" data-id="element-1114">
                            {fullDiagnosticOutput.crossContextView.relationships}
                          </p>
                        </div>
                        <div data-id="element-1115">
                          <h3 className="text-sm font-medium mb-2 text-[#F5F5F5]/80" data-id="element-1116">
                            Decisions
                          </h3>
                          <p className="text-[#F5F5F5]/70 leading-relaxed" data-id="element-1117">
                            {fullDiagnosticOutput.crossContextView.decisions}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Section 6: What Changed from Baseline */}
                <div className="mb-12" data-id="element-1118">
                  <button onClick={() => setShowWhatChanged(!showWhatChanged)} className="flex items-center gap-2 text-base font-medium text-[#F5F5F5] hover:text-[#F5F5F5]/80 transition-colors mb-6" data-id="element-1119">
                    <span data-id="element-1120">🔍</span>
                    <span data-id="element-1121">What changed after deep diagnosis</span>
                    <span className="text-xs" data-id="element-1122">
                      {showWhatChanged ? '▼' : '▶'}
                    </span>
                  </button>
                  {showWhatChanged && <div className="space-y-6" data-id="element-1123">
                      <div className="p-6 bg-[#111214] border border-[#2B2B2B]" data-id="element-1124">
                        <h3 className="text-sm font-medium mb-4" data-id="element-1125">
                          What Quick Self Decode showed
                        </h3>
                        <p className="text-sm text-[#F5F5F5]/70 leading-relaxed" data-id="element-1126">
                          {fullDiagnosticOutput.whatChangedFromSnapshot.quickSelfSnapshot}
                        </p>
                      </div>
                      <div className="p-6 bg-[#111214] border border-[#2B2B2B]" data-id="element-1127">
                        <h3 className="text-sm font-medium mb-4" data-id="element-1128">
                          What deep diagnosis revealed
                        </h3>
                        <p className="text-sm text-[#F5F5F5]/70 leading-relaxed" data-id="element-1129">
                          {fullDiagnosticOutput.whatChangedFromSnapshot.deepDiagnosis}
                        </p>
                      </div>
                      <div className="p-6 bg-[#111214] border border-[#2B2B2B]" data-id="element-1130">
                        <h3 className="text-sm font-medium mb-4" data-id="element-1131">
                          Why snapshot alone was insufficient
                        </h3>
                        <p className="text-sm text-[#F5F5F5]/70 leading-relaxed" data-id="element-1132">
                          {fullDiagnosticOutput.whatChangedFromSnapshot.whySnapshotInsufficient}
                        </p>
                      </div>
                    </div>}
                </div>

                {/* CLOSURE PHASE 1: Pattern Consistency Check */}
                <div className="mb-12 pb-12 border-b border-[#2B2B2B]" data-id="element-1133">
                  <div className="flex items-start gap-4 mb-4" data-id="element-1134">
                    <span className="text-2xl" data-id="element-1135">🔁</span>
                    <div className="flex-1" data-id="element-1136">
                      <h2 className="text-xl font-medium mb-3" data-id="element-1137">
                        Pattern Consistency Check
                      </h2>
                      <p className="text-[#F5F5F5]/70 leading-relaxed mb-6" data-id="element-1138">
                        This same pattern tends to appear when external pressure
                        rises, when clarity is absent, or when control feels
                        threatened. Across different life contexts, the same
                        mechanism repeats.
                      </p>
                      <div className="space-y-4" data-id="element-1139">
                        <div data-id="element-1140">
                          <h3 className="text-sm font-medium mb-2 text-[#F5F5F5]/80" data-id="element-1141">
                            At work
                          </h3>
                          <p className="text-sm text-[#F5F5F5]/60 leading-relaxed" data-id="element-1142">
                            The pattern activates under ambiguous authority,
                            unclear expectations, or when responsibility exceeds
                            control. You over-function to create internal
                            structure, which creates short-term relief but
                            long-term exhaustion.
                          </p>
                        </div>
                        <div data-id="element-1143">
                          <h3 className="text-sm font-medium mb-2 text-[#F5F5F5]/80" data-id="element-1144">
                            In relationships
                          </h3>
                          <p className="text-sm text-[#F5F5F5]/60 leading-relaxed" data-id="element-1145">
                            The pattern appears when emotional intensity rises
                            or when the other person's behavior feels
                            unpredictable. You withdraw into analysis, which
                            creates distance, which reinforces the pattern.
                          </p>
                        </div>
                        <div data-id="element-1146">
                          <h3 className="text-sm font-medium mb-2 text-[#F5F5F5]/80" data-id="element-1147">
                            In decision-making
                          </h3>
                          <p className="text-sm text-[#F5F5F5]/60 leading-relaxed" data-id="element-1148">
                            The pattern delays action when stakes feel high or
                            when multiple variables exist. You seek more
                            information to reduce uncertainty, which delays the
                            decision, which increases pressure.
                          </p>
                        </div>
                        <div data-id="element-1149">
                          <h3 className="text-sm font-medium mb-2 text-[#F5F5F5]/80" data-id="element-1150">
                            Under stress
                          </h3>
                          <p className="text-sm text-[#F5F5F5]/60 leading-relaxed" data-id="element-1151">
                            The pattern intensifies when external chaos
                            increases. You tighten control internally, which
                            creates temporary stability but reduces adaptability
                            and increases rigidity over time.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* CLOSURE PHASE 2: Boundary Conditions */}
                <div className="mb-12 pb-12 border-b border-[#2B2B2B]" data-id="element-1152">
                  <div className="flex items-start gap-4 mb-4" data-id="element-1153">
                    <span className="text-2xl" data-id="element-1154">⚖️</span>
                    <div className="flex-1" data-id="element-1155">
                      <h2 className="text-xl font-medium mb-6" data-id="element-1156">
                        Where This Diagnosis Applies — and Where It Doesn't
                      </h2>
                      <div className="space-y-6" data-id="element-1157">
                        <div data-id="element-1158">
                          <h3 className="text-sm font-medium mb-3 text-[#F5F5F5]/80" data-id="element-1159">
                            This diagnosis explains:
                          </h3>
                          <ul className="space-y-2 text-sm text-[#F5F5F5]/70" data-id="element-1160">
                            <li data-id="element-1161">
                              • Core pattern mechanics and how they formed
                            </li>
                            <li data-id="element-1162">
                              • Repeating internal conflicts across contexts
                            </li>
                            <li data-id="element-1163">
                              • Why certain situations feel disproportionately
                              intense
                            </li>
                            <li data-id="element-1164">
                              • What keeps the pattern active despite awareness
                            </li>
                          </ul>
                        </div>
                        <div data-id="element-1165">
                          <h3 className="text-sm font-medium mb-3 text-[#F5F5F5]/80" data-id="element-1166">
                            This diagnosis does NOT explain:
                          </h3>
                          <ul className="space-y-2 text-sm text-[#F5F5F5]/70" data-id="element-1167">
                            <li data-id="element-1168">• Every emotional reaction or daily mood</li>
                            <li data-id="element-1169">• Every relationship outcome or interaction</li>
                            <li data-id="element-1170">• Temporary phases or situational responses</li>
                            <li data-id="element-1171">
                              • Learned skills, behaviors, or conscious choices
                            </li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* CLOSURE PHASE 3: Integration Summary */}
                <div className="mb-12 pb-12 border-b border-[#2B2B2B]" data-id="element-1172">
                  <div className="flex items-start gap-4 mb-4" data-id="element-1173">
                    <span className="text-2xl" data-id="element-1174">🧭</span>
                    <div className="flex-1" data-id="element-1175">
                      <h2 className="text-xl font-medium mb-3" data-id="element-1176">
                        Integration Summary
                      </h2>
                      <p className="text-[#F5F5F5]/70 leading-relaxed" data-id="element-1177">
                        This is a system that stabilizes through internal
                        structure and control. Under pressure, it tightens,
                        analyzes, and withdraws to create clarity before acting.
                        The pattern keeps repeating because it reliably reduces
                        short-term anxiety, even when it creates long-term
                        friction. Harmony occurs when external conditions allow
                        autonomy and depth; friction occurs when speed,
                        visibility, or ambiguity dominate. The pattern is not
                        the problem — the mismatch between pattern and
                        environment is.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="space-y-4 pt-8 border-t border-[#2B2B2B]" data-id="element-1178">
                  <button onClick={() => navigate('/decode')} className="w-full px-8 py-5 bg-[#F5F5F5] text-[#0A0A0A] font-semibold text-sm tracking-widest uppercase hover:bg-[#F5F5F5]/90 transition-colors shadow-lg" data-id="element-1179">
                    Save Diagnostic
                  </button>
                  <button onClick={() => setCurrentPhase('baseline-output')} className="w-full px-8 py-4 border border-[#F5F5F5]/30 text-[#F5F5F5]/70 font-medium text-sm tracking-wide hover:bg-[#F5F5F5]/5 transition-colors" data-id="element-1180">
                    Revisit Baseline
                  </button>
                </div>
              </div>}
          </div>
        </main>
      </div>
    </>;
}